import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function TextNoteDialog({ open, onClose }) {
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchCategories();
      setContent('');
      setCategory('');
    }
  }, [open]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Errore nel caricamento categorie:', error);
    }
  };

  const handleSave = async () => {
    if (!content.trim() || !category) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/notes`, {
        content,
        category,
      });

      toast.success('Nota salvata con successo!');
      onClose();
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
      toast.error('Errore nel salvataggio della nota');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuova nota scritta</DialogTitle>
          <DialogDescription>
            Crea un promemoria testuale quando non puoi parlare
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="text-content">Contenuto *</Label>
            <Textarea
              id="text-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Scrivi qui il tuo promemoria..."
              className="min-h-[250px] resize-y"
              data-testid="text-note-content-input"
            />
            <p className="text-xs text-muted-foreground">
              {content.length} caratteri
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="text-category">Categoria *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-12" data-testid="text-note-category-select">
                <SelectValue placeholder="Seleziona una categoria" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.name} value={cat.name}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            data-testid="cancel-text-note-button"
          >
            Annulla
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="rounded-full"
            data-testid="save-text-note-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvataggio...
              </>
            ) : (
              'Salva nota'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}